import { useEffect, useRef, useState } from 'react';

import { LambdaLogStreamPoller, type LambdaLogPollFn } from './lambdaLogStreamPoller';

export function useLambdaLogStream(
  pollFn: LambdaLogPollFn | null,
  active: boolean,
  shouldStop?: () => boolean | Promise<boolean>,
  onStreamEnd?: (reason: 'complete' | 'error' | 'aborted') => void,
): { lines: string[]; streamError: string; streaming: boolean } {
  const [lines, setLines] = useState<string[]>([]);
  const [streamError, setStreamError] = useState('');
  const [streaming, setStreaming] = useState(false);
  const pollerRef = useRef<LambdaLogStreamPoller | null>(null);
  const onStreamEndRef = useRef(onStreamEnd);
  onStreamEndRef.current = onStreamEnd;

  useEffect(() => {
    if (!active || !pollFn) {
      pollerRef.current?.stop('aborted');
      pollerRef.current = null;
      setStreaming(false);
      return undefined;
    }

    setLines([]);
    setStreamError('');
    setStreaming(true);

    const poller = new LambdaLogStreamPoller(pollFn, {
      onLine: (line) => setLines((prev) => [...prev, line]),
      onDone: (reason) => {
        setStreaming(false);
        if (reason === 'error') {
          setStreamError('Log stream stopped (check API deployment and admin access).');
        }
        onStreamEndRef.current?.(reason);
      },
      shouldStop,
      pollIntervalMs: 1500,
    });
    pollerRef.current = poller;
    poller.start();

    return () => {
      poller.stop('aborted');
      pollerRef.current = null;
      setStreaming(false);
    };
  }, [active, pollFn, shouldStop]);

  return { lines, streamError, streaming };
}
